import React, { useState, useEffect, useCallback } from 'react';
import ConfirmModal from '../ConfirmModal';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Form, Input, Button, Modal, Select, DatePicker, message, Row, Col, Alert, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { canEditTask } from '../../utils/taskPermissions';
import { useSelector } from 'react-redux';
import { getExistingFilesUploadList, getNewFilesExcludingExisting, uploadTaskFiles } from '../../services/taskFiles';

// Подключаем UTC плагин
dayjs.extend(utc);

const { Option } = Select;
const { TextArea } = Input;

const EditModal = ({ visible, onCancel, onSubmit, task, candidates, users }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const userRole = useSelector((state) => state.auth.role);
  const storedRole = typeof window !== 'undefined' ? localStorage.getItem('user_role') : null;
  const effectiveRole = userRole || storedRole;
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [candidateVacancies, setCandidateVacancies] = useState([]);
  const [requireResult, setRequireResult] = useState(false);
  const [existingFiles, setExistingFiles] = useState([]);

  const currentUserId = parseInt(localStorage.getItem('user_id'));
  const { canEdit, reason } = task
    ? canEditTask(task.creator_id, currentUserId, effectiveRole, task)
    : { canEdit: false, reason: 'Задача не загружена' };

  useEffect(() => {
    if (visible && task) {
      let dueDate = null;
      if (task.deadline) {
        dueDate = dayjs(task.deadline).local();
      }

      form.setFieldsValue({
        ...task,
        due_date: dueDate,
      });

      const initialCandidateId = task.candidate_id || null;
      setSelectedCandidateId(initialCandidateId);
      if (initialCandidateId) {
        const candidate = candidates.find(c => c.id === initialCandidateId);
        const vacancies = candidate?.vacancies || [];
        setCandidateVacancies(vacancies);
        if (task.vacancy_id) {
          form.setFieldsValue({ vacancy_id: task.vacancy_id });
        }
      } else {
        setCandidateVacancies([]);
        form.setFieldsValue({ vacancy_id: undefined });
      }

      setRequireResult(Boolean(task?.result_required));
    }
  }, [visible, task, form, candidates]);

  useEffect(() => {
    const loadFiles = async () => {
      if (!visible || !task?.id) return;
      try {
        const mapped = await getExistingFilesUploadList(task.id);
        setExistingFiles(mapped);
        form.setFieldsValue({ materials: mapped });
      } catch (_) {
        setExistingFiles([]);
      }
    };
    loadFiles();
  }, [visible, task?.id, form]);

  const handleCandidateChange = (candidateId) => {
    setSelectedCandidateId(candidateId);
    const candidate = candidates.find(c => c.id === candidateId);
    const vacancies = candidate?.vacancies || [];
    setCandidateVacancies(vacancies);
    form.setFieldsValue({ vacancy_id: undefined });
  };

  const handleSubmit = async () => {
    if (!canEdit) {
      message.error(reason || 'У вас нет прав для редактирования этой задачи');
      return;
    }

    try {
      setLoading(true);
      const values = await form.validateFields();

      const taskData = {
        description: values.description,
      };

      if (values.due_date) {
        taskData.deadline = `${values.due_date.format('YYYY-MM-DD')}T00:00:00.000Z`;
      }

      if (values.co_assignee_id) {
        taskData.co_assignee_id = parseInt(values.co_assignee_id);
      }
      if (values.observer_id) {
        taskData.observer_id = parseInt(values.observer_id);
      }
      if (values.assignee_id) {
        taskData.assignee_id = parseInt(values.assignee_id);
      }
      if (values.candidate_id) {
        taskData.candidate_id = parseInt(values.candidate_id);
      }
      if (values.vacancy_id) {
        taskData.vacancy_id = parseInt(values.vacancy_id);
      }

      taskData.result_required = requireResult;

      if (values.materials) {
        taskData.materials = values.materials;
      }

      await onSubmit(task.id, taskData);

      // Загрузка новых файлов (без дубликатов по имени)
      const newFiles = getNewFilesExcludingExisting(values.materials || [], existingFiles || []);
      if (newFiles.length > 0) {
        try {
          await uploadTaskFiles(task.id, newFiles);
          message.success('Файлы загружены');
        } catch (_) {
          message.error('Не удалось загрузить некоторые файлы');
        }
      }
    } catch (error) {
      message.error('Ошибка при обновлении задачи');
      console.error('Ошибка:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = useCallback(() => {
    setShowConfirm(true);
  }, []);

  const handleConfirmClose = useCallback((shouldClose) => {
    if (shouldClose) {
      onCancel();
    }
    setShowConfirm(false);
  }, [onCancel]);

  const footerButtons = [
    <Button key="cancel" onClick={handleCancel}>
      Закрыть
    </Button>,
    <Button
      key="submit"
      type="primary"
      loading={loading}
      onClick={handleSubmit}
      disabled={!canEdit}
    >
      Сохранить
    </Button>
  ];

  return (
    <>
      <Modal
        title="Редактирование задачи"
        open={visible}
        onCancel={handleCancel}
        width={1000}
        footer={footerButtons}
      >
        {!canEdit && (
          <Alert
            message="Нет доступа для редактирования"
            description={reason}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <Form form={form} layout="vertical" disabled={!canEdit}>
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Основная информация
            </h3>

            <Form.Item name="candidate_id" label="Кандидат" rules={[{ required: true, message: 'Выберите кандидата!' }]}>
              <Select showSearch optionFilterProp="children" placeholder="Выберите кандидата" onChange={handleCandidateChange}>
                {candidates.map(candidate => (
                  <Option key={candidate.id} value={candidate.id}>
                    {candidate.full_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="vacancy_id"
              label="Вакансия"
              rules={[{ required: true, message: 'Выберите вакансию!' }]}
            >
              <Select
                placeholder={selectedCandidateId ? "Выберите вакансию" : "Сначала выберите кандидата"}
                disabled={!selectedCandidateId || candidateVacancies.length === 0}
              >
                {candidateVacancies.map(vacancy => (
                  <Option key={vacancy.id} value={vacancy.id}>
                    {vacancy.job_title}
                  </Option>
                ))}
              </Select>
              {selectedCandidateId && candidateVacancies.length === 0 && (
                <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                  У выбранного кандидата нет связанных вакансий
                </div>
              )}
            </Form.Item>

            <Form.Item
              name="description"
              label="Описание"
              rules={[{ required: true, message: 'Введите описание задачи!' }]}
            >
              <TextArea rows={4} />
            </Form.Item>

            <Form.Item name="due_date" label="Срок выполнения" rules={[{ required: true, message: 'Выберите срок выполнения!' }]}>
              <DatePicker
                style={{ width: '100%' }}
                format="DD.MM.YYYY"
              />
            </Form.Item>
          </div>

          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Материалы
            </h3>

            <Form.Item name="materials" label="Прикрепленные файлы" valuePropName="fileList" getValueFromEvent={e => Array.isArray(e) ? e : e?.fileList}>
              <Upload
                name="file"
                beforeUpload={() => false}
                multiple
              >
                <Button icon={<UploadOutlined />}>Прикрепить файл</Button>
              </Upload>
            </Form.Item>
          </div>

          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Ответственные лица
            </h3>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="assignee_id" label="Исполнитель" rules={[{ required: true, message: 'Выберите исполнителя!' }]}>
                  <Select showSearch optionFilterProp="children" placeholder="Выберите исполнителя">
                    {users.map(user => (
                      <Option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="co_assignee_id" label="Соисполнитель">
                  <Select showSearch optionFilterProp="children" placeholder="Выберите соисполнителя">
                    {users.map(user => (
                      <Option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="observer_id" label="Наблюдатель">
              <Select showSearch optionFilterProp="children" placeholder="Выберите наблюдателя">
                {users.map(user => (
                  <Option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Дополнительные настройки
            </h3>

            <Form.Item name="result_required" label="Не завершать без результата">
              <Button
                type={requireResult ? "primary" : "default"}
                onClick={() => setRequireResult(!requireResult)}
                style={{
                  width: '100%',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: requireResult ? 'bold' : 'normal'
                }}
              >
                {requireResult ? '✓ Не завершать без результата' : 'Не завершать без результата'}
              </Button>
            </Form.Item>
          </div>
        </Form>
      </Modal>

      <ConfirmModal
        visible={showConfirm}
        onConfirm={handleConfirmClose}
        onCancel={() => setShowConfirm(false)}
        title="Подтверждение закрытия"
        message="Данные не будут сохранены. Продолжить?"
        okText="Продолжить"
        cancelText="Отмена"
        type="warning"
      />
    </>
  );
};

export default EditModal;